import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { corporateCustomersApi } from '../../api/customers.js';
import { usersApi } from '../../api/users.js';
import { areasApi } from '../../api/areas.js';
import { citiesApi } from '../../api/cities.js';

const isTl = (u) => {
  const r = u?.role?.name || u?.role || '';
  return r === 'sales_tl' || r === 'sales_teamlead';
};

const getSalesName = (sales) => {
  if (!sales?.name) return null;
  return `${sales.name}${isTl(sales) ? ' (TL)' : ''}`;
};

// ── Phone cell with copy button ───────────────────────────────────────────────

function PhoneCell({ phone }) {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const numbers = phone ? phone.split(',').map(p => p.trim()).filter(Boolean) : [];
  if (numbers.length === 0) return <span style={{ color: '#94a3b8' }}>—</span>;

  const copy = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {numbers.map((n, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#1e293b' }}>{n}</span>
          <button
            onClick={() => copy(n, i)}
            style={{ background: copiedIdx === i ? '#dcfce7' : '#f1f5f9', border: `1px solid ${copiedIdx === i ? '#86efac' : '#e2e8f0'}`, borderRadius: 4, cursor: 'pointer', padding: '1px 6px', fontSize: 11, color: copiedIdx === i ? '#16a34a' : '#94a3b8', lineHeight: 1.6, flexShrink: 0 }}
          >
            {copiedIdx === i ? '✓' : '⎘'}
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Shared filter components ──────────────────────────────────────────────────

function SearchableStaticSelect({ value, displayName, options, onChange, placeholder, style }) {
  const [search, setSearch]   = useState('');
  const [isOpen, setIsOpen]   = useState(false);
  const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position: 'relative', ...style }}>
      <div
        className="form-control"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ color: value ? '#334155' : '#94a3b8' }}>{value ? displayName : placeholder}</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
      </div>
      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0/0.1)', minWidth: 160 }}>
            <input
              type="text" placeholder="Search..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', background: !value ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#64748b' }}
                onClick={() => { onChange(null); setIsOpen(false); setSearch(''); }}
              >{placeholder}</div>
              {filtered.map(o => (
                <div
                  key={o.id}
                  style={{ padding: '8px 12px', cursor: 'pointer', background: value === o.id ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#334155' }}
                  onClick={() => { onChange(o); setIsOpen(false); setSearch(''); }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = value === o.id ? '#f1f5f9' : 'transparent'}
                >{o.name}</div>
              ))}
              {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: '#94a3b8' }}>No results</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SearchableAreaSelect({ value, areaName, onChange, parentId, style }) {
  const [search, setSearch]   = useState('');
  const [areas, setAreas]     = useState([]);
  const [isOpen, setIsOpen]   = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setAreas([]); }, [parentId]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      setLoading(true);
      const params = { limit: 20, search };
      params.parent_id = parentId || 'not_null';
      areasApi.getAll(params).then(res => setAreas(res.data ?? res)).catch(console.error).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search, isOpen, parentId]);

  return (
    <div style={{ position: 'relative', ...style }}>
      <div
        className="form-control"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ color: value ? '#334155' : '#94a3b8' }}>{value ? areaName || 'Selected Area' : 'All Areas'}</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
      </div>
      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0/0.1)' }}>
            <input
              type="text" placeholder="Search area..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', background: !value ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#64748b' }}
                onClick={() => { onChange(null, null); setIsOpen(false); setSearch(''); }}
              >All Areas</div>
              {loading
                ? <div style={{ padding: '8px 12px', fontSize: 13, color: '#64748b' }}>Loading...</div>
                : areas.map(a => (
                  <div
                    key={a.id}
                    style={{ padding: '8px 12px', cursor: 'pointer', background: value === a.id ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#334155' }}
                    onClick={() => { onChange(a.id, a.name); setIsOpen(false); setSearch(''); }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = value === a.id ? '#f1f5f9' : 'transparent'}
                  >{a.name}</div>
                ))}
              {!loading && areas.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: '#64748b' }}>No areas found</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SearchableSalesSelect({ value, options, onChange, loading, placeholder = 'All Salespersons' }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const filtered = options.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(s => s.id.toString() === value?.toString());
  const displayName = selected ? `${selected.name}${isTl(selected) ? ' (TL)' : ''}` : placeholder;

  return (
    <div style={{ position: 'relative', width: 190 }}>
      <div
        className="form-control"
        onClick={() => !loading && setIsOpen(!isOpen)}
        style={{ cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: loading ? 0.6 : 1 }}
      >
        <span style={{ color: value ? '#334155' : '#94a3b8' }}>{displayName}</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
      </div>
      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0/0.1)' }}>
            <input
              type="text" placeholder="Search person..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', background: !value ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#64748b' }}
                onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
              >{placeholder}</div>
              {filtered.map(s => (
                <div
                  key={s.id}
                  style={{ padding: '8px 12px', cursor: 'pointer', background: value === s.id.toString() ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#334155' }}
                  onClick={() => { onChange(s.id.toString()); setIsOpen(false); setSearch(''); }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = value === s.id.toString() ? '#f1f5f9' : 'transparent'}
                >{s.name}{isTl(s) ? ' (TL)' : ''}</div>
              ))}
              {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: '#94a3b8' }}>No results</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonTable({ isAdmin }) {
  return (
    <>
      <style>{`
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .skeleton-line { background:linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 37%,#f1f5f9 63%); background-size:400% 100%; animation:shimmer 1.4s ease infinite; border-radius:4px; }
      `}</style>
      {[...Array(5)].map((_, i) => (
        <tr key={i}>
          <td><div className="skeleton-line" style={{ width: 140, height: 16 }} /></td>
          <td><div className="skeleton-line" style={{ width: 120, height: 16 }} /></td>
          <td><div className="skeleton-line" style={{ width: 110, height: 16 }} /></td>
          <td><div className="skeleton-line" style={{ width: 130, height: 16 }} /></td>
          <td><div className="skeleton-line" style={{ width: 80,  height: 16 }} /></td>
          {isAdmin && <td><div className="skeleton-line" style={{ width: 96, height: 22, borderRadius: 4 }} /></td>}
        </tr>
      ))}
    </>
  );
}

// ── Edit / Create modal ───────────────────────────────────────────────────────

function CorporateModal({ customer, onClose, onSave }) {
  const [form, setForm] = useState(
    customer
      ? {
          name:           customer.name           || '',
          contact_person: customer.contact_person || '',
          phone:          customer.phone          || '',
          address:        customer.address        || '',
          remark:         customer.remark         || '',
          sales_id:       customer.assigned_to    || '',
          sales_name:     customer.sales?.name    || '',
          city:           customer.city           || 'Kolkata',
        }
      : { name: '', contact_person: '', phone: '', address: '', remark: '', sales_id: '', sales_name: '', city: 'Kolkata' }
  );
  const [saving, setSaving]          = useState(false);
  const [err, setErr]                = useState('');
  const [salesOptions, setSalesOpts] = useState([]);

  useEffect(() => {
    Promise.all([
      usersApi.getAll({ role: 'sales', limit: 200 }),
      usersApi.getAll({ role: 'sales_tl', limit: 200 }),
    ])
      .then(([s, tl]) => setSalesOpts([...(s.data ?? s), ...(tl.data ?? tl)].filter(u => u.is_active !== false)))
      .catch(console.error);
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name?.trim()) { setErr('Client Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        name:           form.name.trim(),
        contact_person: form.contact_person || null,
        phone:          form.phone          || null,
        address:        form.address        || null,
        remark:         form.remark         || null,
        assigned_to:    form.sales_id ? parseInt(form.sales_id, 10) : null,
        city:           form.city || 'Kolkata',
      });
    } catch (e) {
      setErr(e.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 600, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{customer ? 'Edit Corporate Client' : 'Add Corporate Client'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}
          <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Client / Company Name *</label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Company or client name" />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Person</label>
              <input className="form-control" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} placeholder="Name of contact" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone number" />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
            </div>
          </div>
          <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assignment &amp; Notes</div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Assign To</label>
            <select
              className="form-control"
              value={form.sales_id || ''}
              onChange={e => {
                const id = e.target.value;
                const sel = salesOptions.find(s => s.id.toString() === id);
                set('sales_id', id);
                set('sales_name', sel?.name || '');
              }}
            >
              <option value="">Select Salesperson / Team Lead</option>
              {salesOptions.map(s => (
                <option key={s.id} value={s.id}>{s.name}{isTl(s) ? ' (TL)' : ''}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Address</label>
            <textarea className="form-control" rows={2} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Office / branch address" />
          </div>
          <div className="form-group">
            <label className="form-label">Remark / Note</label>
            <textarea className="form-control" rows={2} value={form.remark} onChange={e => set('remark', e.target.value)} placeholder="Status, follow-up notes…" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CorporateCustomersPage({ role }) {
  const isAdmin = role === 'admin';
  const navigate = useNavigate();

  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalCount, setTotal]      = useState(0);
  const [limit]                     = useState(20);
  const [showModal, setShowModal]   = useState(false);
  const [editItem, setEditItem]     = useState(null);

  // ── filter state ────────────────────────────────────────────────────────────
  const [cityFilter,    setCityFilter]    = useState(null);   // { id, name } | null
  const [areaFilter,    setAreaFilter]    = useState(null);   // zone { id, name } | null
  const [subAreaFilter, setSubAreaFilter] = useState(null);   // child area { id, name } | null
  const [salesFilter,   setSalesFilter]   = useState('');
  const [search,        setSearch]        = useState('');

  // ── dropdown data ────────────────────────────────────────────────────────────
  const [cityOptions,  setCityOptions]  = useState([]);
  const [areaOptions,  setAreaOptions]  = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);

  // load cities once
  useEffect(() => {
    citiesApi.getAll()
      .then(res => setCityOptions(res.data || []))
      .catch(console.error);
  }, []);

  // load parent areas on mount
  useEffect(() => {
    areasApi.getAll({ parent_id: 'null', limit: 200 })
      .then(res => setAreaOptions(res.data || []))
      .catch(console.error);
  }, []);

  // city changed → reload zones, reset downstream
  useEffect(() => {
    setAreaFilter(null);
    setSubAreaFilter(null);
    setSalesFilter('');
    const params = cityFilter
      ? { city_id: cityFilter.id, parent_id: 'null', limit: 200 }
      : { parent_id: 'null', limit: 200 };
    areasApi.getAll(params)
      .then(res => setAreaOptions(res.data || []))
      .catch(console.error);
  }, [cityFilter]);

  // area/zone/city → reload sales users
  useEffect(() => {
    setSalesFilter('');
    if (!isAdmin) return;
    setSalesLoading(true);
    const extra = subAreaFilter
      ? { area_id: subAreaFilter.id }
      : areaFilter
        ? { area_id: areaFilter.id }
        : cityFilter
          ? { city_id: cityFilter.id }
          : {};
    Promise.all([
      usersApi.getAll({ role: 'sales',    limit: 200, ...extra }),
      usersApi.getAll({ role: 'sales_tl', limit: 200, ...extra }),
    ])
      .then(([s, t]) => setSalesOptions([...(s.data ?? s), ...(t.data ?? t)].filter(u => u.is_active !== false)))
      .catch(console.error)
      .finally(() => setSalesLoading(false));
  }, [subAreaFilter, areaFilter, cityFilter, isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await corporateCustomersApi.getAll({
        page,
        limit,
        search:      search      || undefined,
        assigned_to: salesFilter || undefined,
        city:        cityFilter?.name || undefined,
      });
      setCustomers(res.data || []);
      setTotal(res.count || 0);
    } catch (e) {
      console.error('Failed to load corporate customers:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [page, search, salesFilter, cityFilter]);

  const handleSave = async (payload) => {
    if (editItem) {
      await corporateCustomersApi.update(editItem.id, payload);
    } else {
      await corporateCustomersApi.create(payload);
    }
    setShowModal(false);
    setEditItem(null);
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this corporate client?')) return;
    try {
      await corporateCustomersApi.delete(id);
      await load();
    } catch (e) { alert(e.message || 'Delete failed.'); }
  };

  const totalPages  = Math.ceil(totalCount / limit) || 1;
  const anyActive   = cityFilter || areaFilter || subAreaFilter || salesFilter || search;
  const activeCount = [cityFilter, areaFilter, subAreaFilter, salesFilter, search].filter(Boolean).length;
  const clearAll    = () => { setCityFilter(null); setAreaFilter(null); setSubAreaFilter(null); setSalesFilter(''); setSearch(''); setPage(1); };

  const LBL = ({ children }) => (
    <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{children}</span>
  );
  const ClearBtn = ({ show, onClick }) => show ? (
    <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 14, lineHeight: 1, padding: '0 0 0 4px' }}>×</button>
  ) : null;

  return (
    <div className="page-body">
      {showModal && (
        <CorporateModal
          customer={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSave={handleSave}
        />
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="card-title">Corporate Customers</span>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Manage corporate &amp; institutional client accounts
            </p>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => navigate('/customer-import')}>↑ Import CSV</button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ Add Client</button>
            </div>
          )}
        </div>

        <div className="card-body">
          {/* ── Filter panel ──────────────────────────────────────────── */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.01em' }}>Filters</span>
                {anyActive && (
                  <span style={{ background: '#3b82f6', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 7px' }}>
                    {activeCount}
                  </span>
                )}
              </div>
              {anyActive && (
                <button onClick={clearAll} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 500, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 14, lineHeight: 1 }}>×</span> Clear all
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>

              {/* City */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <LBL>City</LBL>
                  <ClearBtn show={!!cityFilter} onClick={() => { setCityFilter(null); setPage(1); }} />
                </div>
                <SearchableStaticSelect
                  value={cityFilter?.id || null}
                  displayName={cityFilter?.name || ''}
                  options={cityOptions}
                  onChange={city => { setCityFilter(city); setPage(1); }}
                  placeholder="All Cities"
                  style={{ width: 140 }}
                />
              </div>

              {/* Zone */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <LBL>Zone</LBL>
                  <ClearBtn show={!!areaFilter} onClick={() => { setAreaFilter(null); setSubAreaFilter(null); setSalesFilter(''); setPage(1); }} />
                </div>
                <SearchableStaticSelect
                  value={areaFilter?.id || null}
                  displayName={areaFilter?.name || ''}
                  options={areaOptions}
                  onChange={area => { setAreaFilter(area); setSubAreaFilter(null); setSalesFilter(''); setPage(1); }}
                  placeholder="All Zones"
                  style={{ width: 160 }}
                />
              </div>

              {/* Area */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <LBL>Area</LBL>
                  <ClearBtn show={!!subAreaFilter} onClick={() => { setSubAreaFilter(null); setSalesFilter(''); setPage(1); }} />
                </div>
                <SearchableAreaSelect
                  value={subAreaFilter?.id || null}
                  areaName={subAreaFilter?.name || ''}
                  parentId={areaFilter?.id || null}
                  onChange={(id, name) => { setSubAreaFilter(id ? { id, name } : null); setSalesFilter(''); setPage(1); }}
                  style={{ width: 175 }}
                />
              </div>

              {/* Sales Person */}
              {isAdmin && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <LBL>Sales Person</LBL>
                    <ClearBtn show={!!salesFilter} onClick={() => { setSalesFilter(''); setPage(1); }} />
                  </div>
                  <SearchableSalesSelect
                    value={salesFilter}
                    options={salesOptions}
                    onChange={id => { setSalesFilter(id); setPage(1); }}
                    loading={salesLoading}
                  />
                </div>
              )}

              {/* Search */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <LBL>Search</LBL>
                  <ClearBtn show={!!search} onClick={() => { setSearch(''); setPage(1); }} />
                </div>
                <div className="search-bar" style={{ width: 230, flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input
                    placeholder="Name, phone, contact…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                  />
                </div>
              </div>

            </div>
          </div>

          {/* ── Table ─────────────────────────────────────────────────── */}
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Contact Person</th>
                  <th>Phone</th>
                  <th>Assigned To</th>
                  <th>City</th>
                  {isAdmin && <th style={{ textAlign: 'center' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonTable isAdmin={isAdmin} />
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>
                      No corporate clients found
                    </td>
                  </tr>
                ) : (
                  customers.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ fontSize: 13 }}>{c.contact_person || '—'}</td>
                      <td><PhoneCell phone={c.phone} /></td>
                      <td style={{ fontSize: 13, color: c.sales ? '#334155' : '#94a3b8' }}>
                        {getSalesName(c.sales) || '—'}
                      </td>
                      <td style={{ fontSize: 13 }}>{c.city || '—'}</td>
                      {isAdmin && (
                        <td>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button className="btn btn-outline btn-xs" onClick={() => { setEditItem(c); setShowModal(true); }}>Edit</button>
                            <button className="btn btn-outline btn-xs" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => handleDelete(c.id)}>Delete</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 24, paddingBottom: 8 }}>
              <button className="btn btn-outline" disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>
                Page {page} of {totalPages} &nbsp;({totalCount === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, totalCount)} of {totalCount})
              </span>
              <button className="btn btn-outline" disabled={page >= totalPages || totalCount === 0 || loading} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
